import { Badge, Card, Group, HoverCard, Indicator, Stack, Text, ThemeIcon } from "@mantine/core";
import {
  IconAnchor,
  IconBulb,
  IconBulbOff,
  IconCalendarWeek,
  IconChartCandle,
  IconClockHour4,
  IconClockPlay,
  IconFilter,
  IconHandClick,
  IconHandStop,
  IconHistory,
  IconArrowsUpDown,
  IconArrowsLeftRight,
  IconArrowBarUp,
  IconBrain,
  IconPlayerSkipForward,
  IconShieldHalf,
  IconStack2,
  IconStairs,
  IconTargetArrow,
  IconTrendingDown,
  IconTrendingUp,
  type IconProps,
} from "@tabler/icons-react";
import type { ComponentType } from "react";

import { monoStyle } from "../theme";

type Cfg = Record<string, unknown> | null | undefined;

function rawVal(cfg: Cfg, key: string): unknown {
  if (!cfg) return undefined;
  return (cfg as Record<string, unknown>)[key];
}

function isTruthy(v: unknown): boolean {
  return v === true || v === "true" || v === 1 || v === "1";
}

function num(cfg: Cfg, key: string): number {
  return Number(rawVal(cfg, key) ?? 0) || 0;
}

function fmt(v: unknown): string {
  if (v === undefined || v === null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  return String(v);
}

interface SettingRow {
  label: string;
  value: string;
}

/** Build display rows from a list of [configKey, humanLabel] pairs, dropping empties. */
function rows(cfg: Cfg, entries: Array<[string, string]>): SettingRow[] {
  return entries
    .map(([key, label]) => ({ label, value: fmt(rawVal(cfg, key)) }))
    .filter((r) => r.value !== "—");
}

interface FeatureDef {
  /** Stable id (usually the config enable-flag field). */
  key: string;
  title: string;
  icon: ComponentType<IconProps>;
  color: string;
  settings: (cfg: Cfg) => SettingRow[];
  /**
   * Whether the feature is on. Defaults to the truthiness of `cfg[key]`,
   * but some features (e.g. the candle filter, which is part of Core Entry)
   * are inferred from other fields instead of a dedicated flag.
   */
  enabledFn?: (cfg: Cfg) => boolean;
  /** Show a live "in window / outside window" indicator (time ranges, trade days). */
  windowStatus?: "trade_time" | "trade_days";
}

function featureEnabled(f: FeatureDef, cfg: Cfg): boolean {
  return f.enabledFn ? f.enabledFn(cfg) : isTruthy(rawVal(cfg, f.key));
}

const FEATURES: FeatureDef[] = [
  {
    key: "candle_filter_enabled",
    title: "Candle Filter",
    icon: IconChartCandle,
    color: "yellow",
    // Candle filter lives in Core Entry and has no dedicated flag: it is
    // active whenever at least one candle must match direction.
    enabledFn: (cfg) => num(cfg, "min_matching_candles") > 0,
    settings: (cfg) =>
      rows(cfg, [
        ["candle_timeframe_seconds", "Timeframe (s)"],
        ["skip_first_candles", "Skip first"],
        ["min_matching_candles", "Min matching"],
        ["candles_to_check", "Candles to check"],
        ["allow_live_candle", "Allow live candle"],
        ["live_candle_minute", "Live candle index"],
      ]),
  },
  {
    key: "take_profit_enabled",
    title: "Take Profit",
    icon: IconTargetArrow,
    color: "teal",
    settings: (cfg) =>
      rows(cfg, [
        ["take_profit_threshold", "Threshold"],
        ["take_profit_order_type", "Order type"],
      ]),
  },
  {
    key: "stop_loss_enabled",
    title: "Stop Loss",
    icon: IconHandStop,
    color: "red",
    settings: (cfg) =>
      rows(cfg, [
        ["stop_loss_threshold", "Threshold"],
        ["stop_loss_trigger_within_seconds", "Trigger within (s)"],
        ["stop_loss_btc_delta_enabled", "BTC delta"],
        ["stop_loss_btc_delta_dollars", "BTC delta ($)"],
        ["stop_loss_btc_delta_mode", "BTC delta mode"],
        ["stop_loss_last_candle_instant", "Last-candle instant"],
      ]),
  },
  {
    key: "momentum_sell_enabled",
    title: "Momentum Sell",
    icon: IconTrendingDown,
    color: "orange",
    settings: (cfg) =>
      rows(cfg, [
        ["momentum_sell_window_type", "Window type"],
        ["momentum_sell_window_value", "Window value"],
        ["momentum_sell_sl_enabled", "SL sub-mode"],
        ["momentum_sell_sl_threshold_type", "SL threshold type"],
        ["momentum_sell_sl_threshold_value", "SL threshold"],
        ["momentum_sell_tp_enabled", "TP sub-mode"],
        ["momentum_sell_tp_threshold_type", "TP threshold type"],
        ["momentum_sell_tp_threshold_value", "TP threshold"],
      ]),
  },
  {
    key: "odds_momentum_enabled",
    title: "Momentum (Entry)",
    icon: IconTrendingUp,
    color: "grape",
    settings: (cfg) =>
      rows(cfg, [
        ["odds_momentum_use_unique_odds", "Unique odds"],
        ["odds_momentum_lookback", "Lookback"],
        ["odds_momentum_required_increasing", "Required increasing"],
        ["odds_momentum_ignore_last_seconds", "Ignore last (s)"],
        ["odds_momentum_avg_lookback", "Avg lookback"],
        ["odds_momentum_avg_max_diff", "Avg max diff"],
      ]),
  },
  {
    key: "pre_order_metrics_enabled",
    title: "Pre-order Metrics Filter",
    icon: IconFilter,
    color: "cyan",
    settings: (cfg) => {
      const subs: Array<[string, string, string, string]> = [
        ["pre_order_up_avg_odds_filter_enabled", "pre_order_up_avg_odds_min", "pre_order_up_avg_odds_max", "UP avg odds"],
        ["pre_order_down_avg_odds_filter_enabled", "pre_order_down_avg_odds_min", "pre_order_down_avg_odds_max", "DOWN avg odds"],
        ["pre_order_up_min_odds_filter_enabled", "pre_order_up_min_odds_min", "pre_order_up_min_odds_max", "UP min odds"],
        ["pre_order_up_min_pct_filter_enabled", "pre_order_up_min_pct_min", "pre_order_up_min_pct_max", "UP min Δ%"],
        ["pre_order_down_min_odds_filter_enabled", "pre_order_down_min_odds_min", "pre_order_down_min_odds_max", "DOWN min odds"],
        ["pre_order_down_max_pct_filter_enabled", "pre_order_down_max_pct_min", "pre_order_down_max_pct_max", "DOWN max Δ%"],
      ];
      const out: SettingRow[] = [];
      for (const [en, mn, mx, label] of subs) {
        if (isTruthy(rawVal(cfg, en))) {
          out.push({ label, value: `${fmt(rawVal(cfg, mn))} – ${fmt(rawVal(cfg, mx))}` });
        }
      }
      return out;
    },
  },
  {
    key: "open_price_gate_enabled",
    title: "Open-price Direction Gate",
    icon: IconArrowsUpDown,
    color: "lime",
    settings: () => [],
  },
  {
    key: "open_price_override_enabled",
    title: "Open-price Candle Override",
    icon: IconArrowBarUp,
    color: "teal",
    settings: (cfg) => {
      const within = num(cfg, "open_price_override_within_seconds");
      return [{ label: "Trigger", value: within > 0 ? `last ${within}s` : "right away" }];
    },
  },
  {
    key: "open_price_cushion_enabled",
    title: "Open-price Cushion Gate",
    icon: IconArrowsUpDown,
    color: "lime",
    settings: (cfg) => {
      const minBps = num(cfg, "open_price_cushion_min_bps");
      return [{ label: "Min cushion", value: `${minBps} bps` }];
    },
  },
  {
    key: "prev_windows_check_enabled",
    title: "Previous Windows Direction",
    icon: IconHistory,
    color: "indigo",
    settings: (cfg) =>
      rows(cfg, [
        ["prev_windows_count", "Windows checked"],
        ["prev_windows_min_matches", "Min matches"],
      ]),
  },
  {
    key: "pre_decision_avg_odds_enabled",
    title: "Pre-decision Average Odds",
    icon: IconFilter,
    color: "blue",
    settings: (cfg) => rows(cfg, [["pre_decision_avg_odds_min", "Min frozen average odds"]]),
  },
  {
    key: "band_dwell_enabled",
    title: "Time in Min \u2194 Max Odds Range Before Decision Time",
    icon: IconFilter,
    color: "blue",
    settings: (cfg) => {
      const out = rows(cfg, [
        ["band_dwell_min_seconds", "Min time in range (s)"],
      ]);
      if (isTruthy(rawVal(cfg, "band_dwell_require_exceeds_opposite"))) {
        out.push({ label: "Must exceed opposite side", value: "yes" });
      }
      return out;
    },
  },
  {
    key: "flip_gate_enabled",
    title: "Max Favorite Flips",
    icon: IconFilter,
    color: "blue",
    settings: (cfg) =>
      rows(cfg, [
        ["flip_gate_max_flips", "Max flips"],
        ["flip_gate_skip_seconds", "Skip first (s)"],
        ["flip_gate_flip_threshold", "Flip threshold"],
      ]),
  },
  {
    key: "kronos_check_enabled",
    title: "Kronos Forecast",
    icon: IconBrain,
    color: "grape",
    settings: (cfg) =>
      rows(cfg, [
        ["kronos_model", "Model"],
        ["kronos_min_confidence", "Min confidence"],
        ["kronos_require_direction", "Require direction"],
        ["kronos_lookback", "Lookback"],
        ["kronos_candle_timeframe_seconds", "Candle TF (s)"],
        ["kronos_sample_count", "Samples"],
      ]),
  },
  {
    key: "counterpart_override_enabled",
    title: "Counterpart Market Override",
    icon: IconArrowsLeftRight,
    color: "orange",
    settings: (cfg) => {
      const within = num(cfg, "counterpart_decide_within_pct");
      const out = [
        { label: "Odds threshold", value: fmt(rawVal(cfg, "counterpart_odds_threshold")) },
        { label: "Decide within", value: within > 0 ? `${within}% left` : "anytime" },
        { label: "Stable secs", value: fmt(rawVal(cfg, "counterpart_stable_seconds")) },
        { label: "Require trend", value: fmt(rawVal(cfg, "counterpart_require_trend")) },
        { label: "Follow direction", value: fmt(rawVal(cfg, "counterpart_follow_direction")) },
        { label: "Exclusive entries", value: fmt(rawVal(cfg, "counterpart_follow_exclusive")) },
      ];
      const bypass: string[] = [];
      for (const [k, label] of [
        ["counterpart_bypass_candle", "candle"],
        ["counterpart_bypass_open_price", "open-price"],
        ["counterpart_bypass_prev_windows", "prev-windows"],
        ["counterpart_bypass_kronos", "kronos"],
        ["counterpart_bypass_momentum", "momentum"],
        ["counterpart_bypass_pre_order", "pre-order"],
        ["counterpart_bypass_time_ranges", "time-ranges"],
        ["counterpart_bypass_min_odds", "min-odds"],
        ["counterpart_bypass_max_odds", "max-odds"],
      ] as const) {
        if (isTruthy(rawVal(cfg, k))) bypass.push(label);
      }
      out.push({ label: "Bypasses", value: bypass.length ? bypass.join(", ") : "none" });
      return out;
    },
  },
  {
    key: "counterpart_match_order_enabled",
    title: "Counterpart Matching Order",
    icon: IconArrowsLeftRight,
    color: "orange",
    settings: (cfg) => {
      const requires: string[] = [];
      for (const [k, label] of [
        ["counterpart_match_order_require_trend", "trend"],
        ["counterpart_match_order_require_candle", "candle"],
        ["counterpart_match_order_require_open_price", "open-price"],
        ["counterpart_match_order_require_prev_windows", "prev-windows"],
        ["counterpart_match_order_require_kronos", "kronos"],
        ["counterpart_match_order_require_momentum", "momentum"],
        ["counterpart_match_order_require_pre_order", "pre-order"],
        ["counterpart_match_order_require_time_ranges", "time-ranges"],
        ["counterpart_match_order_require_min_odds", "min-odds"],
        ["counterpart_match_order_require_max_odds", "max-odds"],
      ] as const) {
        if (isTruthy(rawVal(cfg, k))) requires.push(label);
      }
      return [{ label: "Requires", value: requires.length ? requires.join(", ") : "none (place immediately)" }];
    },
  },
  {
    key: "favorite_hold_enabled",
    title: "Favorite Hold (3-Tier)",
    icon: IconAnchor,
    color: "teal",
    settings: (cfg) => {
      const out: SettingRow[] = [
        { label: "Flip threshold", value: fmt(rawVal(cfg, "favorite_hold_flip_threshold")) },
      ];
      const fhSkip = rawVal(cfg, "favorite_hold_skip_seconds");
      if (isTruthy(fhSkip)) {
        out.push({ label: "Skip first", value: `${fmt(fhSkip)}s` });
      }
      if (isTruthy(rawVal(cfg, "favorite_hold_tier1_enabled"))) {
        out.push({
          label: "Tier 1 (dwell)",
          value: `>= ${fmt(rawVal(cfg, "favorite_hold_tier1_min_odds"))} for ${fmt(rawVal(cfg, "favorite_hold_tier1_dwell_seconds"))}s`,
        });
      }
      if (isTruthy(rawVal(cfg, "favorite_hold_tier2_enabled"))) {
        out.push({
          label: "Tier 2 (rise)",
          value: `rise >= ${fmt(rawVal(cfg, "favorite_hold_tier2_min_rise"))} @ ${fmt(rawVal(cfg, "favorite_hold_tier2_check_seconds"))}s`,
        });
        const requires: string[] = [];
        for (const [k, label] of [
          ["favorite_hold_tier2_require_candle", "candle"],
          ["favorite_hold_tier2_require_open_price", "open-price"],
          ["favorite_hold_tier2_require_prev_windows", "prev-windows"],
          ["favorite_hold_tier2_require_kronos", "kronos"],
          ["favorite_hold_tier2_require_momentum", "momentum"],
          ["favorite_hold_tier2_require_pre_order", "pre-order"],
          ["favorite_hold_tier2_require_time_ranges", "time-ranges"],
          ["favorite_hold_tier2_require_min_odds", "min-odds"],
        ] as const) {
          if (isTruthy(rawVal(cfg, k))) requires.push(label);
        }
        out.push({ label: "Tier 2 requires", value: requires.length ? requires.join(", ") : "none" });
      }
      if (isTruthy(rawVal(cfg, "favorite_hold_tier3_enabled"))) {
        const bypass: string[] = [];
        for (const [k, label] of [
          ["favorite_hold_tier3_bypass_candle", "candle"],
          ["favorite_hold_tier3_bypass_open_price", "open-price"],
          ["favorite_hold_tier3_bypass_prev_windows", "prev-windows"],
          ["favorite_hold_tier3_bypass_kronos", "kronos"],
          ["favorite_hold_tier3_bypass_momentum", "momentum"],
          ["favorite_hold_tier3_bypass_pre_order", "pre-order"],
          ["favorite_hold_tier3_bypass_time_ranges", "time-ranges"],
          ["favorite_hold_tier3_bypass_min_odds", "min-odds"],
        ] as const) {
          if (isTruthy(rawVal(cfg, k))) bypass.push(label);
        }
        out.push({ label: "Tier 3 bypasses", value: bypass.length ? bypass.join(", ") : "none" });
      }
      return out;
    },
  },
  {
    key: "hedge_enabled",
    title: "Hedge at Entry",
    icon: IconShieldHalf,
    color: "yellow",
    settings: (cfg) => rows(cfg, [["hedge_pct", "Hedge % of trade size"]]),
  },
  {
    key: "skip_after_wins_enabled",
    title: "Win-streak Skip",
    icon: IconPlayerSkipForward,
    color: "blue",
    settings: (cfg) =>
      rows(cfg, [
        ["skip_after_wins_threshold", "Wins before skip"],
        ["skip_after_wins_losses_to_resume", "Losses to resume"],
      ]),
  },
  {
    key: "cascading_sizes_enabled",
    title: "Cascading Sizes",
    icon: IconStairs,
    color: "pink",
    settings: (cfg) => rows(cfg, [["cascading_sizes", "Sizes"]]),
  },
  {
    key: "compounding_enabled",
    title: "Compounding Sizing",
    icon: IconStack2,
    color: "green",
    settings: (cfg) =>
      rows(cfg, [
        ["compounding_win_pct", "Win %"],
        ["compounding_max_size_usdc", "Max size ($)"],
        ["compounding_stop_after_wins", "Stop after wins"],
        ["compounding_stop_size_stay", "Stay at size"],
        ["compounding_resume_from_saved", "Resume from saved"],
      ]),
  },
  {
    key: "late_buy_enabled",
    title: "Late Buy",
    icon: IconClockPlay,
    color: "lime",
    settings: (cfg) =>
      rows(cfg, [
        ["late_buy_within_seconds", "Within (s)"],
        ["late_buy_trade_size_usdc", "Trade size ($)"],
      ]),
  },
  {
    key: "trade_time_ranges_enabled",
    title: "Trade Time Ranges",
    icon: IconClockHour4,
    color: "violet",
    windowStatus: "trade_time",
    settings: (cfg) => {
      const raw = fmt(rawVal(cfg, "trade_time_ranges"));
      const count = raw === "—" ? 0 : raw.split(",").filter((s) => s.trim()).length;
      const out: SettingRow[] = [];
      if (count) out.push({ label: "Ranges configured", value: String(count) });
      if (isTruthy(rawVal(cfg, "trade_time_ranges_split_results"))) {
        out.push({ label: "Split results", value: "Yes" });
      }
      return out;
    },
  },
  {
    key: "trade_days_enabled",
    title: "Trade Days",
    icon: IconCalendarWeek,
    color: "cyan",
    windowStatus: "trade_days",
    settings: (cfg) => rows(cfg, [["trade_days_allowed", "Allowed days"]])
      .map((row) => ({ ...row, value: row.value.split(",").map((day) => day.trim()).join(", ") })),
  },
  {
    key: "force_buy_enabled",
    title: "Dashboard Manual Buy",
    icon: IconHandClick,
    color: "blue",
    settings: () => [],
  },
];

export interface FeatureIconStripProps {
  config: Cfg;
  title?: string;
  /** Whether we are currently inside an allowed trade time range (null = unknown). */
  tradeTimeInWindow?: boolean | null;
  /** Whether today is an allowed trade day (null = unknown). */
  tradeDaysInWindow?: boolean | null;
}

/**
 * Horizontal strip of feature icons. Each enabled feature shows its themed
 * colour; disabled features are greyed out. Hovering any icon opens a neat
 * popover listing that feature's current settings.
 */
export function FeatureIconStrip({
  config,
  title = "Conditional Checks",
  tradeTimeInWindow = null,
  tradeDaysInWindow = null,
}: FeatureIconStripProps) {
  const enabledCount = FEATURES.reduce((n, f) => (featureEnabled(f, config) ? n + 1 : n), 0);

  const windowStatusFor = (f: FeatureDef): boolean | null => {
    if (f.windowStatus === "trade_time") return tradeTimeInWindow;
    if (f.windowStatus === "trade_days") return tradeDaysInWindow;
    return null;
  };

  return (
    <Card withBorder padding="sm">
      <Group justify="space-between" mb="xs">
        <Text size="xs" c="dimmed" fw={700} tt="uppercase">{title}</Text>
        <Text size="xs" c="dimmed">{enabledCount} of {FEATURES.length} enabled</Text>
      </Group>
      <Group gap="xs" wrap="wrap">
        {FEATURES.map((f) => {
          const on = featureEnabled(f, config);
          const settings = f.settings(config);
          const Icon = f.icon;
          const winStatus = on ? windowStatusFor(f) : null;
          const showDot = winStatus != null;
          return (
            <HoverCard
              key={f.key}
              width={264}
              shadow="md"
              openDelay={80}
              closeDelay={60}
              withArrow
              position="bottom"
            >
              <HoverCard.Target>
                <Indicator
                  disabled={!showDot}
                  color={winStatus ? "green" : "red"}
                  size={10}
                  offset={4}
                  withBorder
                  processing={showDot && winStatus === true}
                >
                  <ThemeIcon
                    size={40}
                    radius="md"
                    variant={on ? "light" : "default"}
                    color={on ? f.color : "gray"}
                    style={{ opacity: on ? 1 : 0.4, cursor: "help" }}
                  >
                    <Icon size={22} stroke={1.6} />
                  </ThemeIcon>
                </Indicator>
              </HoverCard.Target>
              <HoverCard.Dropdown>
                <Stack gap={8}>
                  <Group justify="space-between" wrap="nowrap">
                    <Text fw={700} size="sm">{f.title}</Text>
                    {on ? (
                      <IconBulb size={20} stroke={1.6} color="var(--mantine-color-yellow-5)" />
                    ) : (
                      <IconBulbOff size={20} stroke={1.6} color="var(--mantine-color-gray-6)" />
                    )}
                  </Group>
                  {on ? (
                    <Stack gap={6}>
                      {showDot ? (
                        <Badge
                          size="sm"
                          variant="light"
                          color={winStatus ? "green" : "gray"}
                          leftSection={
                            <span
                              style={{
                                display: "inline-block",
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: winStatus
                                  ? "var(--mantine-color-green-5)"
                                  : "var(--mantine-color-gray-5)",
                              }}
                            />
                          }
                        >
                          {winStatus ? "Currently in window" : "Currently outside window"}
                        </Badge>
                      ) : null}
                      {settings.length ? (
                        <Stack gap={3}>
                          {settings.map((r) => (
                            <Group key={r.label} justify="space-between" align="flex-start" gap="sm" wrap="nowrap">
                              <Text size="xs" c="dimmed" maw="50%" style={{ flexShrink: 0 }}>{r.label}</Text>
                              <Text size="xs" fw={600} miw={0} style={{ ...monoStyle, overflowWrap: "anywhere" }} ta="right">{r.value}</Text>
                            </Group>
                          ))}
                        </Stack>
                      ) : showDot ? null : (
                        <Text size="xs" c="dimmed">No additional settings.</Text>
                      )}
                    </Stack>
                  ) : (
                    <Text size="xs" c="dimmed">Turned off for this config.</Text>
                  )}
                </Stack>
              </HoverCard.Dropdown>
            </HoverCard>
          );
        })}
      </Group>
    </Card>
  );
}
